class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    /* `dangCho` đặt ĐỒNG BỘ ở cú gõ, debounce bắn thì xoá: đó là cách duy nhất biết một lượt mới
       sắp tới trong khoảng 300ms này, lúc mà chữ đã đổi nhưng chưa có fetch nào. */
    const goTre = debounce((event) => {
      if (!this.dangCho) return;  // hộp đã đóng trong lúc chờ: rời ô hoặc Escape
      this.dangCho = false;
      this.onChange(event);
    }, 300).bind(this);
    this.input.addEventListener('input', (event) => {
      this.dangCho = true;
      goTre(event);
    });
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
      /* 2026-08-17 — Escape KHÔNG đóng được ô gợi ý. Grep 'Escape' trong file
         này + main-search.liquid + header.liquid ra 0 kết quả: mở dropdown ra
         rồi chỉ đóng được bằng cách xoá sạch ô hoặc bấm ra ngoài. Khách dùng
         bàn phím và trình đọc màn hình bị nhốt trong danh sách.
         KHÔNG truyền `true`: Escape là "đóng gợi ý", không phải "xoá thứ tôi
         vừa gõ" — xoá luôn ô nhập là cướp mất công gõ của khách. */
      case 'Escape':
        this.close();
        this.input.focus();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    /* 2026-08-17 — khoá cache cũ là `searchTerm.replace(" ", "-")`: String
       .replace với một CHUỖI chỉ thay dấu cách ĐẦU TIÊN. Nên "hot sauce mix"
       và "hot-sauce mix" ra cùng một khoá `hot-sauce mix` ⇒ đụng nhau, gõ cái
       này hiện kết quả cái kia. Regex /\s+/g mới thay hết. */
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    /* 🔴 17/8 vòng 15 — LỆNH HUỶ PHẢI ĐỨNG TRƯỚC CỬA THOÁT CỦA BỘ ĐỆM.
       Bản vòng 14 dựng đúng cơ chế AbortController nhưng đặt nó SAU khối kiểm
       đệm, nên nhánh TRÚNG ĐỆM vẽ xong rồi `return` luôn — chưa bao giờ chạy
       tới lệnh huỷ. Trình tự làm sập, dựng lại được trên mạng chậm:
           gõ "pho"   → lượt A bay đi, về, được ướp đệm
           gõ "phong" → lượt B bay đi, mạng chậm nên treo
           xoá 1 chữ về "pho" → TRÚNG ĐỆM, vẽ ngay rồi return; B KHÔNG bị huỷ
           vài trăm ms sau B về → ghi đè
       Kết quả: ô nhập đang là "pho" mà danh sách gợi ý là của "phong", dòng
       cuối ghi «Search for "phong"» — bấm vào là bị đưa sang kết quả của từ
       khách KHÔNG gõ. Không có gì báo.
       Huỷ lượt cũ là việc phải làm BẤT KỂ lượt mới lấy từ đệm hay từ mạng. */
    if (this.abortCtl) this.abortCtl.abort();
    this.abortCtl = typeof AbortController === 'function' ? new AbortController() : null;
    /* Số lượt: close() và lượt mới đều tăng số này, nên lượt nào về mà lệch số thì không còn
       quyền vẽ, không còn quyền đóng. AbortController không rút được `fetch` đã xong và trình
       duyệt cũ không có nó, nên không dựa riêng vào nó. Người gác: scripts/test-giao-hang-tim-kiem-chay-that.js */
    const luot = this.luot = (this.luot || 0) + 1;

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    /* `tag` TRONG DANH SÁCH TRƯỜNG — GIỮ LẠI, NHƯNG KHÔNG PHẢI VÌ LÝ DO GHI BAN ĐẦU.
     *
     * ❌ ĐÍNH CHÍNH 18/8/2026 (đo lại ngay trong ngày, 4 lượt phản biện độc lập).
     *   Chú thích cũ ở đây khẳng định: "predictive search mặc định KHÔNG tìm trong
     *   `tag`, thiếu tham số này là 1.642 thẻ tiếng Việt nằm chết", kèm bảng
     *   "0 → 10" cho 6 từ khoá. **Bảng đó không tái hiện được, và mệnh đề đó sai.**
     *
     *   Bằng chứng (cửa hàng thật, tất cả đều 200, đã chống đệm bằng nonce ngẫu
     *   nhiên, đã đảo thứ tự gọi, `cf-cache-status: DYNAMIC`, không có header `age`,
     *   mỗi lượt một request-id riêng ⇒ không phải hiện tượng đệm):
     *
     *     · MẶC ĐỊNH ĐÃ TÌM TRONG THẺ. "mộc nhĩ" chỉ nằm trong `tags` của
     *       Richin Black Fungus (tiêu đề "Richin Black Fungus 2.5oz", vendor
     *       "Richin", type "Seasoning"). Gõ "mộc nhĩ" mà xin **fields=vendor một
     *       mình** vẫn ra đúng món đó. Tương tự "pocky" + fields=vendor vẫn ra
     *       Glico Pocky, dù "pocky" chỉ có trong TIÊU ĐỀ.
     *     · 25+ chuỗi thẻ đã kiểm là KHÔNG trùng bất kỳ title/vendor/product_type/
     *       variant-title nào trong catalog — bỏ tham số / có tham số / chỉ xin
     *       `tag`: kết quả TRÙNG TỪNG BYTE, trùng cả thứ tự.
     *     · Không phải sai hình dạng tham số: `[]` cho kết quả y hệt, endpoint
     *       `.json` không kèm `section_id` cũng y hệt, và một tuỳ chọn ANH EM cùng
     *       độ sâu (`resources[options][unavailable_products]=hide`) thì ĐỔI được
     *       kết quả ⇒ Shopify có phân tích ngoặc, chỉ riêng `fields` là vô hiệu
     *       cho truy vấn chữ thường.
     *     · Tên trường sai (`fields=xxxnope`) trả **HTTP 422** ⇒ tham số được
     *       kiểm cú pháp, rồi bỏ qua khi khớp.
     *     · 🔴 24/8/2026 — ĐO THÊM, và đây là mảnh còn thiếu: gợi ý CÓ dò trong
     *       MÔ TẢ sản phẩm. Chọn từ "mcillhenny" — quét 2.628 SP thì nó KHÔNG có
     *       trong title/tag/vendor/product_type của bất cứ món nào, chỉ nằm trong
     *       `body_html` của đúng một món (Tabasco Original). `suggest.json?q=
     *       mcillhenny` CÓ kèm fields vẫn trả về Tabasco; bỏ fields cũng y hệt.
     *       ⇒ `fields` không những không thu hẹp được, mà thứ nó KHÔNG chặn được
     *       lại chính là trường bẩn nhất. Hệ quả thật: 65,3% mô tả (1.716/2.628)
     *       có tiếng Việt KHÔNG DẤU, nên `suggest.json?q="ngan keo"` trả về
     *       [Eagle] Medicated Oil ở hạng #1. Chữ "keo" trong "ngăn kéo" và trong
     *       "kẹo" là MỘT sau khi Shopify bỏ dấu — và nó bỏ dấu cả hai chiều, nên
     *       thêm dấu vào dữ liệu KHÔNG cứu được gì.
     *       ⛔ Đừng đi thêm tham số vào truy vấn để chữa: đã đo, vô ích. Lối ra
     *       duy nhất là sửa chữ trong mô tả, hoặc đưa khối tiếng Việt sang
     *       metafield (metafield không bị đánh chỉ mục tìm kiếm).
     *
     * ⚠️ VÌ SAO VẪN GIỮ `tag` — CÂU TRẢ LỜI ĐÃ ĐỔI, 14/9/2026 (vòng 48).
     *
     *   Chú thích cũ ở đây khẳng định: tham số `tag` có tác dụng ở đúng một chỗ —
     *   thẻ mang dấu hai chấm (`q="cuisine:filipino"` không xin tag → 0 SP · có
     *   xin tag → 10 SP). ĐO LẠI HÔM NAY THÌ KHÔNG CÒN ĐÚNG. Ba lượt mỗi đường,
     *   nonce chống đệm, cùng một giây:
     *     cuisine:filipino → không-tag 10 thẻ · có-tag 10 thẻ · GIỐNG TỪNG BYTE
     *     cuisine:korean   → không-tag 10 thẻ · có-tag 10 thẻ · GIỐNG TỪNG BYTE
     *     zzqq:filipino    → không-tag 10 thẻ · có-tag 10 thẻ · GIỐNG TỪNG BYTE
     *   Kể cả ô ĐỐI CHỨNG cũng ra 10. Tức tham số này nay VÔ TÁC DỤNG, và câu
     *   "cứ có dấu hai chấm là rỗng" cũng sai nốt.
     *
     *   Việc tra THẺ thì vẫn chạy — nhưng chạy MẶC ĐỊNH, không nhờ tham số này.
     *   Đo bằng từ chỉ nằm trong `tags`, không nằm trong title/vendor/type nào:
     *     "mộc nhĩ" · "nấm mèo" · "wood ear" → đều ra đúng họ mộc nhĩ, ở CẢ HAI
     *     đường, và không món nào chứa từ khoá trong TÊN.
     *
     *   ✅ VẪN GIỮ tham số: nó không hại gì, và nếu Shopify lật lại hành vi cũ
     *     thì nó đã sẵn ở đó. Nhưng ĐỪNG đọc nó thành "nhờ nó mà tra được thẻ".
     *     `scripts/test-search-doc-the.js` nay canh KẾT QUẢ (gõ từ tiếng Việt chỉ
     *     nằm trong thẻ phải ra đúng món, từ vô nghĩa phải ra rỗng) chứ không canh
     *     LỜI GỌI nữa. [[reference_do_ket_qua_dung_do_loi_goi]]
     *
     * 🟡 CÒN MỘT CHỖ HỎNG, của Shopify, KHÔNG vá ở theme: MỘT tiếng duy nhất dạng
     *   `x:y` làm trang /search bỏ qua câu hỏi và trả cả kho (chặn ở 1000) —
     *   "qwxzptnk" → 0 results, mà "qwxzptnk:abc" → 1000 results. Đừng chữa bằng
     *   cách cắt dấu hai chấm khỏi câu hỏi: tiền tố HỢP LỆ vẫn lọc đúng
     *   ("tag:filipino" → 47 · "title:Milo" → 6), cắt là giết đường đang chạy.
     *   Câu NHIỀU CHỮ có dấu hai chấm vẫn đúng ("Nissin Cup Noodles: Seafood" →
     *   54 · "Size: 500g" → 26) — mà đó mới là thứ khách thật sự gõ.
     *
     * 🔴 ĐIỀU BOSS BÁO THÌ VẪN CHƯA CHỮA. "gõ tofu ra gợi ý không phải tofu":
     *   cả kho chỉ có 4 món dính chữ tofu/bean curd và KHÔNG món nào là đậu hũ
     *   tươi — Shopify đang trả về đúng thứ tốt nhất nó có. Nhưng gõ "đậu hũ"
     *   thì 4 món bean curd bị đẩy xuống hạng 8, trên đầu là Stir Fry Seasoning
     *   và Cup Noodles ⇒ câu tiếng Việt xếp hạng TỆ HƠN câu tiếng Anh. Đó là
     *   chuyện của thứ tự liên quan, không phải của tham số này. Cần cấu hình
     *   Synonyms trong app Search & Discovery (Shopify không có API — chỉ Boss
     *   bật được). */
    /* 🔴 25/9/2026 (vòng 53, search#4) — DÁN LINK SẢN PHẨM ⇒ 4/4 GỢI Ý SAI.
       `https://aefoodstore.com/products/<handle>` là MỘT tiếng liền dạng `x:y`
       ⇒ đúng cái bẫy "cả kho" ghi ở trên: đo LIVE ra Milo, Maxim, Lay's ×2, không
       món nào là món khách dán. Bỏ riêng `https://` thì 2/2 đúng.
       Câu gửi đi do bộ dịch quyết (`aefCauGoiY` trong assets/aef-search-rewrite.js —
       MỘT nguồn nhận diện link, đừng chép regex sang đây): link của tiệm ⇒ chữ
       của handle, link lạ ⇒ cắt đầu link, câu thường ⇒ NGUYÊN VĂN.
       Tra `window.AefSearchRewrite` LÚC GỌI: layout nạp tệp này TRƯỚC tệp kia, cả
       hai defer. Không có bộ dịch thì gửi như cũ.
       `queryKey` và phép so `dangGo` bên dưới GIỮ trên chữ THÔ của ô nhập — đổi
       chúng sang câu đã đổi là mọi lượt vẽ bị bỏ vì lệch khoá.
       Enter / nút "Search for" vẫn gửi form với chữ khách dán ⇒ móc submit của bộ
       dịch đưa thẳng tới trang sản phẩm. Người gác: scripts/test-dan-link-vao-o-tim.js */
    let cauHoi = searchTerm;
    try {
      const boDich = window.AefSearchRewrite;
      if (boDich && typeof boDich.aefCauGoiY === 'function') cauHoi = boDich.aefCauGoiY(searchTerm) || searchTerm;
    } catch (e) {}
    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(cauHoi)}&${encodeURIComponent('resources[type]')}=product&${encodeURIComponent('resources[limit]')}=4&${encodeURIComponent('resources[options][fields]')}=title,product_type,variants.title,vendor,tag&section_id=predictive-search`,
      this.abortCtl ? { signal: this.abortCtl.signal } : undefined
    )
      .then((response) => {
        if (!response.ok) throw new Error(response.status);

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        /* LỚP CHẶN THỨ HAI. AbortController lo được lượt do CHÍNH mình bắn đi,
           nhưng nó không lo được mọi đường: `fetch` đã giải quyết xong thì
           `abort()` không rút lại được nữa, và trình duyệt cũ không có
           AbortController (this.abortCtl = null). So khoá lượt với thứ khách
           ĐANG gõ trước khi vẽ — lệch thì bỏ, vẫn ướp đệm để lát nữa dùng lại. */
        const dangGo = this.getQuery().replace(/\s+/g, '-').toLowerCase();
        if (luot !== this.luot) return;
        /* Chữ đã đổi. Debounce còn chờ thì lượt mới sắp tới, giữ vòng quay kẻo hộp lật đóng mở
           mỗi cú gõ chậm. Không còn gì chờ thì gỡ, để lại là vòng quay kẹt. */
        if (dangGo !== queryKey) {
          if (!this.dangCho) this.removeAttribute('loading');
          return;
        }
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        /* Lượt bị CHÍNH MÌNH huỷ không phải lỗi — đóng ô gợi ý ở đây là đóng
           đúng cái lượt mới vừa mở ra, và ném tiếp thì console đầy AbortError
           trong lúc mọi thứ đang chạy đúng. */
        if (error && error.name === 'AbortError') return;
        if (luot !== this.luot) return;
        /* Lỗi của lượt này không phải ý khách: cú gõ còn chờ vẫn phải được hỏi. */
        this.close(false, true);
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');

    /* 🔴 17/8 vòng 15 — `data-loading-text` do `{{ 'accessibility.loading' | t }}`
       sinh ra ở thẻ <predictive-search> của sections/main-search.liquid (KHÔNG ghi số
       dòng: chú thích cũ ghi :26 đã sai, bản vá 21/9 thay bằng :316 thì trong CÙNG
       buổi hôm đó nó đã trôi tiếp sang 401 vì tệp ấy đang được sửa song song — viết
       số vào đây là hẹn sẵn một lần sai nữa. Tra bằng chuỗi `data-loading-text`;
       sections/header.liquid cũng sinh y hệt cho ô header),
       tức bám locale storefront chứ không bám
       ae_lang. Khách Việt nghe «Loading» giữa một trang tiếng Việt.
       Và ĐỪNG cache như bản cũ (`this.loadingText = this.loadingText || …`):
       khách đổi ngôn ngữ giữa phiên thì câu đọc lên phải đổi theo, cache là
       giữ luôn thứ tiếng của lần gõ đầu tiên. Vẫn giữ thuộc tính làm dự phòng
       cho trường hợp từ điển chưa nạp xong. */
    var txt = (typeof window.aefT === 'function' && window.aefT('psearch.loading'))
      || this.getAttribute('data-loading-text');

    this.setLiveRegionText(txt);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    /* Nội dung vừa thay là markup MỚI TINH do máy chủ trả về — nó chưa đi qua
       lượt dịch nào. Không gọi lại thì mọi [data-i18n] trong đó đứng nguyên
       tiếng Anh dù khách đang bật tiếng Việt. Cùng cách cart drawer đã làm sau
       khi nạp lại thân giỏ. Hàm được phơi ở ae-delivery-widget.js:1497
       (`window.applyTranslations = applyTranslations` — số dòng cũ ghi :973 đã trôi,
       đo lại 21/9). */
    if (typeof window.applyTranslations === 'function') {
      try { window.applyTranslations(); } catch (e) {}
    }

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  /* 🔴 21/9/2026 — CÔNG TẮC NÂNG TẦNG XẾP CỦA HEADER TRONG LÚC HỘP GỢI Ý MỞ.
     ═══════════════════════════════════════════════════════════════════════════
     VÌ SAO CẦN. Hộp gợi ý của header nằm trong `.aef-sticky-wrap`
     (khai trong khối <style> THƯỜNG ở <head> của layout/theme.liquid — `position:
     sticky; z-index: 100`; tra bằng TÊN LỚP, số dòng của tệp đó trôi liên tục.
     ⚠️ 21/9 lượt soi lại: bản vá đầu ghi «khối `{% style %}`» là SAI KHỐI — theme.liquid
     có HAI khối CSS ở <head>, `{% style %}` của Liquid chỉ chứa bảng biến `:root`, còn
     `.aef-sticky-wrap` nằm ở khối `<style>` HTML thường bên dưới. Tìm nhầm khối là
     tưởng luật chưa có rồi khai lại lần hai), tức một NGỮ CẢNH
     XẾP LỚP riêng. `z-index: 300` mà component-predictive-search.css đặt cho hộp chỉ
     đua được với anh em TRONG header; so với phần còn lại của trang cả hộp vẫn chỉ
     đáng 100. Mà `.aef-satc` — thanh mua-nhanh dính đáy PDP, `position: fixed;
     z-index: 998`, nằm thẳng trong thân trang — thì 998, nên nó VẼ ĐÈ lên dải đáy
     của hộp gợi ý.

     ĐO ĐƯỢC GÌ (21/9, PDP thật trên LIVE, `document.elementFromPoint` — đo KẾT QUẢ,
     không so z-index trên giấy; popup chào mừng gỡ trước khi đo vì nó z-index 99999):
         375×553 (iPhone SE2/8 Safari)  chồng 8,5px  → 10/10 điểm trúng DIV.aef-satc
         360×560 (Android nhỏ Chrome)   chồng 8,5px  → 10/10 điểm trúng DIV.aef-satc
         390×664 (iPhone 12/13/14)      chồng 1,5px  →  9/9  điểm trúng DIV.aef-satc
         320×454 (iPhone SE1)           chồng 28,1px → có điểm trúng BUTTON.aef-satc__btn
     Ở 375×553 có một DÒNG GỢI Ý THẬT ló 9px xuống dải đó ⇒ khách chạm vào một gợi ý
     mà trúng nút "Thêm vào giỏ" của thanh kia, tức THÊM MÓN KHÁC vào giỏ.

     ⚠️ BẪY: ở khổ 390×844 (mặc định của playwright — màn hình KHÔNG có thanh công cụ,
     không máy thật nào vậy) chồng nhau ĐÚNG 0px và lỗi biến mất hoàn toàn. Hai lượt
     soi trước đã bác bỏ lỗi này vì đo ở khổ đó. Muốn dựng lại thì đặt chiều cao bằng
     `innerHeight` THẬT của trình duyệt di động, và nhớ `.aef-satc` chỉ có ở ≤767px.

     ⛔ ĐỪNG chữa bằng cách nâng z-index của chính cái hộp — số bao nhiêu cũng vô nghĩa
     khi đã bị nhốt trong tầng xếp. Phải nâng CHÍNH CÁI TẦNG ĐANG NHỐT, và chỉ nâng
     trong lúc hộp mở. Đúng khuôn bảng ZIP đã dùng từ vòng 24
     (`html.aef-dw-sheet-open .aef-sticky-wrap`) — dùng lại, không đẻ cách thứ hai.
     Luật CSS đi kèm: `html.aef-psearch-open .aef-sticky-wrap { z-index: 9050 }`
     trong assets/component-predictive-search.css. Chỉ có luật mà không ai bật công
     tắc thì bản vá là số 0, và ngược lại. */
  capNhatTangXep() {
    /* Hỏi CẢ TRANG, đừng hỏi riêng `this`. Một trang CÓ THỂ có nhiều hơn một phần tử
       <predictive-search>: sections/header.liquid bọc ô ở header, còn
       sections/main-search.liquid bọc ô của chính trang /search. Nếu close() cứ gỡ
       class vô điều kiện thì cái đóng sau gỡ mất công tắc của cái đang MỞ.
       (Không ghi số dòng của hai tệp kia: chúng đang được sửa, số dòng trôi ngay.)
       Đọc thẳng DOM chứ không nuôi một biến đếm: biến đếm sẽ lệch vĩnh viễn nếu có
       phần tử bị gỡ khỏi trang lúc đang mở, và không ai thấy. */
    var goc = document.documentElement;
    /* KHÔNG dùng `classList.toggle(ten, co)`: Safari cũ bỏ qua tham số thứ hai và
       LẬT ngược trạng thái — đúng loại hỏng câm không có log. add/remove thì mọi
       trình duyệt hiểu như nhau. */
    if (document.querySelector('predictive-search[open]')) goc.classList.add('aef-psearch-open');
    else goc.classList.remove('aef-psearch-open');
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    /* Gọi SAU setAttribute: capNhatTangXep() đọc `predictive-search[open]` từ DOM,
       gọi trước là nó đếm hụt chính phần tử này. */
    this.capNhatTangXep();
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false, giuCho = false) {
    /* 01/11/2026: 429 hoặc mất mạng đi vào đây mà `loading` còn nguyên, và CSS mở hộp theo
       `[loading]` nên vòng quay kẹt mãi. Đóng cũng phải huỷ lượt đang bay và cú gõ còn chờ debounce,
       nếu không gợi ý về sau đó tự bung lại dù focus đã ra ngoài. Phần chưa kịp vẽ thì gỡ `results`,
       lần focus sau hỏi lại cho đúng chữ đang gõ. */
    this.luot = (this.luot || 0) + 1;
    if (this.abortCtl) { this.abortCtl.abort(); this.abortCtl = null; }
    if (this.hasAttribute('loading') || this.dangCho) this.removeAttribute('results');
    this.removeAttribute('loading');
    if (!giuCho) this.dangCho = false;

    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('open');
    /* Cũng SAU removeAttribute, cùng lý do như trong open(): hàm đọc lại DOM. */
    this.capNhatTangXep();
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
